/**
 * Tells staging apart from production, and makes the difference impossible to
 * miss in a browser tab.
 *
 * Both panels render identically, so two open tabs are indistinguishable —
 * same title, same favicon, same colours — and the only way to know which one
 * you are about to change a live restaurant in is to read the address bar.
 *
 * Derived from the hostname rather than a build-time variable on purpose: it
 * needs no configuration, cannot be forgotten when a new environment appears,
 * and cannot be wrong. Anything that is not the production domain is treated as
 * not-production, which fails in the safe direction — a mislabelled staging is
 * harmless, a staging that looks like production is not.
 */
const PRODUCTION_HOSTS = ['admin.vellocy.com']

export const isProduction = (): boolean =>
  PRODUCTION_HOSTS.includes(window.location.hostname)

export const environmentName = (): string =>
  isProduction() ? 'production' : 'staging'

const STAGING_COLOUR = '#B45309'

/**
 * Draws a favicon at runtime instead of shipping a second icon file, so the two
 * environments build from one identical artifact.
 */
const stagingFavicon = (): string | null => {
  const canvas = document.createElement('canvas')
  canvas.width = 64
  canvas.height = 64
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  ctx.fillStyle = STAGING_COLOUR
  ctx.fillRect(0, 0, 64, 64)
  ctx.fillStyle = '#FFFFFF'
  ctx.font = 'bold 40px -apple-system, Helvetica, Arial, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('S', 32, 36)

  return canvas.toDataURL('image/png')
}

export const applyEnvironmentChrome = (): void => {
  if (isProduction()) return

  document.title = 'STAGING — Vello Admin'

  const href = stagingFavicon()
  if (href) {
    document.querySelectorAll("link[rel~='icon']").forEach((el) => el.remove())
    const link = document.createElement('link')
    link.rel = 'icon'
    link.type = 'image/png'
    link.href = href
    document.head.appendChild(link)
  }

  // A band across the very top, above everything the app draws. Thin enough to
  // ignore while working, impossible to miss when glancing at the window.
  const banner = document.createElement('div')
  banner.textContent = 'STAGING — test data, safe to break'
  banner.setAttribute('role', 'status')
  Object.assign(banner.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    zIndex: '2147483647',
    background: STAGING_COLOUR,
    color: '#FFFFFF',
    font: '500 12px/20px -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
    letterSpacing: '0.08em',
    textAlign: 'center',
    height: '20px',
    pointerEvents: 'none'
  })
  document.body.appendChild(banner)
  document.body.style.paddingTop = '20px'
}
